import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Button, DeviceSettingsButton, getDeviceStatusText, PopupList, ReportIcon, RightCaretIcon, RowsContainer, SingleRowButtonWithIcon, } from '@streamr/tv-ui';
import { useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { COMPLAINT_REPORT_URL } from '../../consts';
import { MediaService } from '../../services/media/MediaService';
import { BottomRight, Popup } from '../../utils/popups';
import { Anchor } from '../Anchor';
import { HideGlobalScrollbar } from '../shared/HideGlobalScrollbar';
import { AbstractPopup, DismissalReason, } from './AbstractPopup';
import { selectCamera } from './devices/CameraSelectorPopup';
import { selectMicrophone } from './devices/MicrophoneSelectorPopup';
import { ScrollablePopupContainer } from './helpers/ScrollablePopupContainer';
import { about, help } from './InfoPopup';
import { PopupCssPreset } from './Popup.styles';
import { showTermsOfServicePopup } from './TermsOfServicePopup';
export function SettingsPopup({ reject, resolve, ...props }) {
    const selectedVideoDevice = MediaService.getOutput().useSelectedVideoDevice();
    const selectedAudioDevice = MediaService.getOutput().useSelectedAudioDevice();
    const isVideoDisabled = MediaService.getOutput().useVideoMuted();
    const isAudioDisabled = MediaService.getOutput().useAudioMuted();
    const videoPermissioned = MediaService.getOutput().useCameraPermissioned();
    const audioPermissioned = MediaService.getOutput().useMicrophonePermissioned();
    const noAudioDevices = MediaService.getOutput().useAudioDevices().length === 0;
    const noVideoDevices = MediaService.getOutput().useVideoDevices().length === 0;
    const microphoneStatusText = useMemo(() => getDeviceStatusText(selectedAudioDevice, noAudioDevices, audioPermissioned), [selectedAudioDevice, noAudioDevices, audioPermissioned]);
    const cameraStatusText = useMemo(() => getDeviceStatusText(selectedVideoDevice, noVideoDevices, videoPermissioned), [selectedVideoDevice, noVideoDevices, videoPermissioned]);
    const microphoneButtonAnchorRef = useRef(null);
    const [microphoneSelectorOpen, setMicrophoneSelectorOpen] = useState(false);
    const cameraButtonAnchorRef = useRef(null);
    const [cameraSelectorOpen, setCameraSelectorOpen] = useState(false);
    return (_jsxs(SettingsPopupRoot, { reject: (reason) => {
            if (microphoneSelectorOpen || cameraSelectorOpen) {
                return;
            }
            reject(reason);
        }, ...props, translateFn: BottomRight, children: [_jsx(ScrollablePopupContainer, { onCloseButtonClick: () => {
                    reject(DismissalReason.CloseButton);
                }, children: _jsxs(SettingsPopupContent, { children: [_jsx(Anchor, { ref: microphoneButtonAnchorRef, children: _jsx(DeviceSettingsButton, { icon: "microphone", label: "Microphone", statusText: microphoneStatusText, isActive: !isAudioDisabled, displayErrorIcon: !audioPermissioned || noAudioDevices, onClick: () => {
                                    selectMicrophone({
                                        anchorRef: microphoneButtonAnchorRef,
                                        variant: 'bottom-center',
                                        onOpenPopup: () => {
                                            setMicrophoneSelectorOpen(true);
                                        },
                                        onClosePopup: () => {
                                            setMicrophoneSelectorOpen(false);
                                        },
                                    });
                                } }) }), _jsx(Anchor, { ref: cameraButtonAnchorRef, children: _jsx(DeviceSettingsButton, { icon: "camera", label: "Camera", statusText: cameraStatusText, isActive: !isVideoDisabled, displayErrorIcon: !videoPermissioned || noVideoDevices, onClick: () => {
                                    selectCamera({
                                        anchorRef: cameraButtonAnchorRef,
                                        variant: 'bottom-center',
                                        onOpenPopup: () => {
                                            setCameraSelectorOpen(true);
                                        },
                                        onClosePopup: () => {
                                            setCameraSelectorOpen(false);
                                        },
                                    });
                                } }) }), _jsxs(StyledPopupList, { children: [_jsxs(ListElement, { type: "button", onClick: () => {
                                        help();
                                        resolve();
                                    }, children: [_jsx("span", { children: "Help" }), _jsx(StyledCaredIcon, {})] }), _jsxs(ListElement, { type: "button", onClick: () => {
                                        about();
                                        resolve();
                                    }, children: [_jsx("span", { children: "About" }), _jsx(StyledCaredIcon, {})] }), _jsxs(ListElement, { type: "button", onClick: () => {
                                        // biome-ignore lint/nursery/noFloatingPromises: TODO
                                        showTermsOfServicePopup({
                                            mode: 'informative',
                                        });
                                        resolve();
                                    }, children: [_jsx("span", { children: "Terms of service" }), _jsx(StyledCaredIcon, {})] })] }), _jsx(SingleRowButtonWithIcon, { label: "Report", icon: _jsx(StyledReportIcon, {}), onClick: () => {
                                resolve();
                                window.open(COMPLAINT_REPORT_URL, '_blank');
                            } })] }) }), _jsx(HideGlobalScrollbar, {})] }));
}
const SettingsPopupRoot = styled(AbstractPopup) `
    ${PopupCssPreset.Fullscreen};
    --Popup_Padding: 0;

    @media (min-width: 30rem) {
        ${PopupCssPreset.AnchoredBottomRight};
        --Popup_Padding: 1rem 0;
        --Popup_Width: 25rem;
    }
`;
const StyledCaredIcon = styled(RightCaretIcon) `
    opacity: 0.5;
`;
const StyledReportIcon = styled(ReportIcon) `
    opacity: 0.5;
`;
const SettingsPopupContent = styled(RowsContainer) `
    width: 100%;
    gap: 1rem;

    ${Anchor} {
        width: auto;
    }
`;
const StyledPopupList = styled(PopupList) `
    --PopupListItem_Padding: 0;
`;
const ListElement = styled(Button) `
    --Button_HoverTransform: 0;
    --Button_ActiveTransform: 0;
    --Button_Padding: 1rem;
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
`;
const settingsPopup = Popup.create(SettingsPopup);
export const showSettingsPopup = async (options) => {
    try {
        await settingsPopup.pop(options);
    }
    catch (_) { }
};
